import type { ReactNode } from 'react'
import type { SessionSummary, Show } from '@/lib/server/shows'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Media } from '@/components/generals/media'
import { dateKey, formatKey, formatTime } from '@/lib/time'

interface ShowPosterProps {
    show: Show
    upcoming: SessionSummary[]
    todayKey: string
    onBook: () => void
    index: number
    className?: string
}

export const sessionDayLabel = (session: SessionSummary, todayKey: string, tomorrowKey: string): string => {
    const key = dateKey(new Date(session.startsAt))

    if (key === todayKey) return 'Today'
    if (key === tomorrowKey) return 'Tomorrow'

    return formatKey(key, 'short')
}

/** A tall cinematic poster for one show, with its next few start times. */
export const ShowPoster = ({ show, upcoming, todayKey, onBook, index, className }: ShowPosterProps): ReactNode => {
    const tomorrowKey = (() => {
        const [year, month, day] = todayKey.split('-').map(Number)
        const next = new Date(Date.UTC(year, month - 1, day + 1))

        return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`
    })()

    return (
        <article className={cn('group relative flex flex-col', className)}>
            <div className='relative'>
                <Media
                    src={show.poster ?? '/images/planetarium-theatre.jpg'}
                    alt={show.posterAlt}
                    sizes='(min-width: 1024px) 33vw, 100vw'
                    className='aspect-[3/4] w-full'
                    imageClassName='transition-transform duration-[2400ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover:scale-[1.04]'
                >
                    <span aria-hidden className='absolute inset-0 z-[1] bg-gradient-to-t from-black via-black/40 to-black/10' />
                    <span aria-hidden className='absolute inset-x-6 top-6 z-[2] flex items-center justify-between'>
                        <span className='label-mono text-star-2'>
                            {`No. ${String(index + 1).padStart(2, '0')}`}
                        </span>
                        <span className='label-mono text-star-2'>
                            {show.kind}
                        </span>
                    </span>
                    <span aria-hidden className='absolute inset-x-6 top-14 z-[2] h-px bg-gradient-to-r from-brass/0 via-brass/60 to-brass/0' />
                    <span className='absolute inset-x-6 bottom-6 z-[2] block'>
                        <span className='block font-heading text-5xl font-light leading-[0.95] text-star md:text-[3.4rem]'>
                            {show.title}
                        </span>
                        <span className='mt-4 block max-w-xs text-[0.95rem] leading-snug text-star-2'>
                            {show.tagline}
                        </span>
                        <span className='label-mono mt-5 flex flex-wrap gap-x-4 gap-y-1 text-star-3'>
                            <span>
                                {`${show.duration} minutes`}
                            </span>
                            <span>
                                {show.ageLabel}
                            </span>
                        </span>
                    </span>
                </Media>
                <button
                    type='button'
                    onClick={onBook}
                    className='absolute inset-0 z-[5] block w-full'
                    aria-label={`Book ${show.title}`}
                />
            </div>

            <div className='mt-5 flex items-start justify-between gap-4'>
                <div>
                    <p className='label-mono text-star-3'>
                        Next showing
                    </p>
                    <ul className='data-mono mt-2 space-y-1 text-star-2'>
                        {upcoming.slice(0, 3).map(session => (
                            <li key={session.id}>
                                {`${sessionDayLabel(session, todayKey, tomorrowKey)} · ${formatTime(new Date(session.startsAt))}`}
                            </li>
                        ))}
                        {upcoming.length === 0 && (
                            <li>
                                New dates coming soon
                            </li>
                        )}
                    </ul>
                </div>
                <button
                    type='button'
                    onClick={onBook}
                    className='label-mono group/book inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-brass/60 px-5 text-star transition-colors duration-500 hover:bg-brass hover:text-ink'
                >
                    Book
                    <ArrowUpRight aria-hidden className='size-3.5 transition-transform duration-500 group-hover/book:-translate-y-0.5 group-hover/book:translate-x-0.5' strokeWidth={1.25} />
                </button>
            </div>
        </article>
    )
}
